import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { UserRound } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useEmbajadorImpersonation } from "@/contexts/EmbajadorImpersonationContext";
import { useAmbassadors } from "@/store/AmbassadorsContext";
import { SelectorVerComo, type GrupoVerComo } from "@/components/ui/selector-ver-como";

/**
 * Selector "Ver como" del header del Portal Embajadores, pintado con el estándar
 * `SelectorVerComo`. El modo de vista (completa / del usuario) va en la prop `modo`: el
 * conmutador arriba de la lista mientras se impersona, mismo lugar en todos los portales.
 */
export function EmbajadorImpersonationSelector() {
  const { profile } = useAuth();
  const {
    impersonatedEmbajadorId,
    impersonatedEmbajadorName,
    setImpersonatedEmbajador,
    clearImpersonation,
    isImpersonating,
  } = useEmbajadorImpersonation();
  const { ambassadors } = useAmbassadors();

  // Fuente de verdad del acceso: la bandera del rol, no una lista de rol_id.
  // El layout gateaba por `rol_id === 1 || 2` y dejaba fuera a Admin Soporte (30),
  // que sí tiene `puede_impersonar` en BD.
  const canImpersonate = profile?.puede_impersonar === true;

  const embajadores = useMemo(
    () =>
      ambassadors
        .filter((e) => e.status !== "inactivo")
        .slice()
        .sort((a, b) => a.fullName.localeCompare(b.fullName, "es")),
    [ambassadors]
  );

  const emails = useMemo(
    () => embajadores.map((e) => e.email).filter(Boolean) as string[],
    [embajadores]
  );

  // El store de embajadores no conoce la cuenta de sistema. Sin `rol_id` no hay
  // vista fiel y sin `auth_user_id` toda consulta por `auth.uid()` seguiría
  // resolviendo con el admin, así que se resuelven aquí de una sola pasada.
  const { data: cuentas = {} } = useQuery({
    queryKey: ["embajadores-cuentas-impersonacion", emails],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("usuarios")
        .select("email, rol_id, auth_user_id")
        .in("email", emails);
      if (error) throw error;
      const map: Record<string, { rolId: number | null; authUserId: string | null }> = {};
      for (const u of data || []) {
        map[String(u.email).toLowerCase()] = {
          rolId: u.rol_id ?? null,
          authUserId: u.auth_user_id ?? null,
        };
      }
      return map;
    },
    enabled: canImpersonate && emails.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  if (!canImpersonate) return null;

  const etiqueta = isImpersonating ? impersonatedEmbajadorName || "Embajador" : "Mi vista";

  const grupos: GrupoVerComo[] = [];
  grupos.push({
    titulo: "Embajadores",
    opciones: embajadores.map((emb) => {
      const cuenta = cuentas[(emb.email || "").toLowerCase()];
      return {
        key: String(emb.id),
        label: emb.fullName,
        // El buscador filtra por nombre, código y correo.
        buscarPor: `${emb.code ?? ""} ${emb.email ?? ""}`,
        nota: emb.status === "pendiente" ? "Pendiente" : emb.code || undefined,
        seleccionada: impersonatedEmbajadorId === emb.id,
        onSelect: () =>
          setImpersonatedEmbajador({
            id: emb.id,
            name: emb.fullName,
            code: emb.code ?? null,
            personaId: emb.idPersona ?? null,
            email: emb.email ?? null,
            rolId: cuenta?.rolId ?? null,
            authUserId: cuenta?.authUserId ?? null,
          }),
      };
    }),
  });

  return (
    <SelectorVerComo
      etiqueta={etiqueta}
      impersonando={isImpersonating}
      iconoVistaPropia={<UserRound className="size-3.5" strokeWidth={1.75} />}
      opcionPorDefecto={{
        key: "__mi_vista__",
        label: "Mi vista",
        nota: "Por defecto",
        seleccionada: !isImpersonating,
        onSelect: clearImpersonation,
        icono: <UserRound className="mr-2 size-3.5 shrink-0 text-primary" />,
      }}
      grupos={grupos}
      onLimpiar={clearImpersonation}
      ariaLimpiar="Dejar de impersonar"
      textoVacio="No se encontró el embajador."
    />
  );
}
